import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { AuthLayout } from '../layouts/AuthLayout';
import { Alert, Field } from '../components/Field';
import { api, type ApiError } from '../api/client';

export function Register() {
  const [form, setForm] = useState({ firstName: '', lastName: '', email: '', password: '' });
  const [error, setError] = useState<ApiError | null>(null);
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  function update(key: keyof typeof form) {
    return (event: { target: { value: string } }) =>
      setForm((current) => ({ ...current, [key]: event.target.value }));
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api('/auth/register', { method: 'POST', body: JSON.stringify(form) });
      setDone(true);
    } catch (caught) {
      setError(caught as ApiError);
    } finally {
      setBusy(false);
    }
  }

  if (done) {
    return (
      <AuthLayout>
        <h2 className="auth__title">Check your email</h2>
        <Alert
          kind="success"
          title={`If ${form.email} is not already registered, a verification link is on its way. The link is valid for 24 hours.`}
        />
        <p className="auth__footnote">
          <Link to="/login">Back to sign in</Link>
        </p>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <h2 className="auth__title">Create your account</h2>
      <p className="auth__lede">Start learning in a few minutes.</p>

      {error ? <Alert kind="error" title={error.message} items={error.details} /> : null}

      <form onSubmit={onSubmit} noValidate>
        <Field label="First name" required value={form.firstName} onChange={update('firstName')} />
        <Field label="Last name" required value={form.lastName} onChange={update('lastName')} />
        <Field
          label="Email address"
          type="email"
          autoComplete="username"
          required
          value={form.email}
          onChange={update('email')}
        />
        <Field
          label="Password"
          type="password"
          autoComplete="new-password"
          required
          hint="At least 10 characters, using three of: lowercase, uppercase, digit, symbol."
          value={form.password}
          onChange={update('password')}
        />

        <button className="btn" type="submit" disabled={busy}>
          {busy ? 'Creating…' : 'Create account'}
        </button>
      </form>

      <p className="auth__footnote">
        Already have an account? <Link to="/login">Sign in</Link>
      </p>
    </AuthLayout>
  );
}
